import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable, of, from, BehaviorSubject } from 'rxjs';
import { catchError, map, share, switchMap } from 'rxjs/operators';
import { LoginModel } from '../models/login.model';
import { TokenModel } from '../models/token.model';
import { SessionModel } from '../models/session.model';
import { ForgotPasswordModel } from '../models/forgot-password.model';
import { ResetPasswordModel } from '../models/reset-password.model';
import { EventService } from './event.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { ConfigService } from 'app/services/config.service';
import * as Sentry from '@sentry/browser';
import { UnsubscribeModel } from '../models/unsubscribe.model';
import { EmailModel } from '../models/email.model';

const jwtHelper = new JwtHelperService();

@Injectable()
export class MembershipService {
          public get redirectUrl(): string | null {
          return sessionStorage.getItem('redirectUrl');
      }

      public set redirectUrl(value: string | null) {
              if (value === null) {
                sessionStorage.removeItem('redirectUrl');
              } else {
                sessionStorage.setItem('redirectUrl', value);
              }
      }
    public no_local_storage = false;
        private token_change_subject: BehaviorSubject<TokenModel | null> = new BehaviorSubject<TokenModel | null>(null);
    public token_change$: Observable<TokenModel | null> = this.token_change_subject.asObservable();
    constructor(private http: HttpClient, private router: Router, private event_service: EventService, private config: ConfigService) {
        if (this.testLocalStorage()) {
            this.load_user();
        } else {
            this.no_local_storage = true;
        }
    }

    private load_user() {
        this.GetToken().subscribe((token) => {
            if (!token) {
                  this.token_change_subject.next(null);
                return;
            }
            const jwt = jwtHelper.decodeToken(token.token);
            if (!jwt) {
                  this.token_change_subject.next(null);
                return;
            }
             this.token_change_subject.next(token);
            Sentry.configureScope(function (scope) {
                scope.setUser({ 'email': jwt['email'] });
            });

        });
    }

    public Login(argument: LoginModel): Observable<TokenModel> {
        let url = 'api/v3/session/login';
        if (argument.external_token) {
            url = 'api/v3/session/external-login';
        }
        return this.http.post<TokenModel>(this.config.getApiRoute(url), argument).pipe(
            map(data => {
                return data;
            }),
            catchError((err: HttpErrorResponse) => {
                return throwError(err.error.message);
            }),
            share());

    }

    public ForgotPassword(argument: ForgotPasswordModel): Observable<{}> {
        return this.http.post(this.config.getApiRoute('api/v3/session/forgot-password'), argument).pipe(
            map(data => {
                return data;
            }),
            catchError((err: HttpErrorResponse) => {
                return throwError(err.error.message);
            }),
            share());

    }

    public ResetPassword(argument: ResetPasswordModel): Observable<{}> {
        return this.http.post(this.config.getApiRoute('api/v3/session/reset-password'), argument).pipe(
            map(data => {
                return data;
            }),
            catchError((err: HttpErrorResponse) => {
                return throwError(err.error.message);
            }),
            share());

    }
    public ValidatePasswordLink(argument: ResetPasswordModel): Observable<ResetPasswordModel> {
      return this.http.post<ResetPasswordModel>(this.config.getApiRoute('api/v3/session/validate-password-link'), argument).pipe(
          map(data => {
              return data;
          }),
          catchError((err: HttpErrorResponse) => {
              return throwError(err.error.message);
          }),
          share());

  }

  public UnsubscribeEmail(email_token: string, argument: UnsubscribeModel): Observable<UnsubscribeModel> {
      return this.http.post<UnsubscribeModel>(this.config.getApiRoute('api/v3/session/unsubscribe'), argument).pipe(
          map(data => {
              return data;
          }),
          catchError((err: HttpErrorResponse) => {
              return throwError(err.error.message);
          }),
          share());
  }

  public ResubscribeEmail(email_token: string, argument: UnsubscribeModel): Observable<UnsubscribeModel> {
      return this.http.post<UnsubscribeModel>(this.config.getApiRoute('api/v3/session/resubscribe'), argument).pipe(
          map(data => {
              return data;
          }),
          catchError((err: HttpErrorResponse) => {
              return throwError(err.error.message);
          }),
          share());
  }

  public ResendPasswordLink(argument: ResetPasswordModel): Observable<ResetPasswordModel> {
    return this.http.post<ResetPasswordModel>(this.config.getApiRoute('api/v3/session/resend-password-link'), argument).pipe(
        map(data => {
            return data;
        }),
        catchError((err: HttpErrorResponse) => {
            return throwError(err.error.message);
        }),
        share());

}

    public SetToken(token: TokenModel) {
        if (this.no_local_storage) { return; };
        localStorage.setItem('token', JSON.stringify(token));
        this.load_user();
        this.event_service.sessionStart(new SessionModel(token));
    }

    public ClearToken() {
        if (this.no_local_storage) { return; };
        localStorage.removeItem('token');
        this.load_user();
        this.event_service.sessionEnd();
    }

    private testLocalStorage(): boolean {
        const test = 'test';
        try {
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }

    public GetToken(): Observable<TokenModel> {
        if (this.no_local_storage) { return of(null); };
        if (localStorage.getItem('token') && localStorage.getItem('token').startsWith('{')) {
            const model: TokenModel = JSON.parse(localStorage.getItem('token'));

            if (jwtHelper.isTokenExpired(model.token)) {
                this.ClearToken();
                this.redirectUrl = this.router.url;
                this.router.navigate(['/login']);
            }
            return of(model);
        }
        else {
            return of(null);
        }
    }

    public OverrideUtilitiesLinked() {
        if (localStorage.getItem('token')) {
            const token = JSON.parse(localStorage.getItem('token'));
            token.utilities_linked = true;
            localStorage.setItem('token', JSON.stringify(token));
        }
    }

    public OverrideGoodStanding() {
        if (localStorage.getItem('token')) {
            const token = JSON.parse(localStorage.getItem('token'));
            token.good_standing = true;
            localStorage.setItem('token', JSON.stringify(token));
        }
    }

    public OverrideEnrollmentComplete() {
        if (localStorage.getItem('token')) {
            const token = JSON.parse(localStorage.getItem('token'));
            token.enrollment_complete = true;
            localStorage.setItem('token', JSON.stringify(token));
        }
    }

    public GetSession(): Observable<SessionModel> {
        return from(this.GetToken()).pipe(
            switchMap((token: TokenModel) => {
                let session: SessionModel = null;
                if (token && token.token) {
                    session = new SessionModel(token);
                }
                return of(session);
            }),
            catchError(error => {
                return of(null);
            }),
            share()
        );
    }

    public isValidToken() {
        if (localStorage.getItem('token') && localStorage.getItem('token').startsWith('{')) {
            const model: TokenModel = JSON.parse(localStorage.getItem('token'));
            if (!jwtHelper.isTokenExpired(model.token)) {
                return true;
            }
            else{
                return false;
            }
        }
        else{
            return false;
        }
    }


    public RedirectToLogin(){
        this.ClearToken();
        this.redirectUrl = this.router?.url;
        this.router.navigate(['/login']);
    }
       public redirect_last_visited_page(){
        if (this.redirectUrl) {
                const route =this.redirectUrl;
                this.redirectUrl=null;
                this.router.navigate([route]);
        } else{
           this.router.navigate(['/']);
        }
    }
}
